import { AutoCompleteInputProps } from 'lib/components/Form/AutoCompleteInput/AutoCompleteInput'
import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import { autoCompleteInputRules } from 'lib/utilities/autoCompleteInputUtils'
import {
  hasUnsupportedCountry,
  isRouteUnavailable,
} from 'lib/utilities/bookingWidgetUtils'
import { useCallback, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import Button from '../Button/Button'
import { AutoCompleteInput } from '../Form'
import Notification from '../Notification/Notification'
import { TripTypeContent } from './collapsibles/BookingWidgetTripType'
import { BookingWidgetContentProps } from './partials/BookingWidgetContent'
import { useBookingWidgetStore } from './providers/BookingWidgetContext'

export const BOOKING_WIDGET_MODAL_ID = 'booking-widget'

export interface BookingWidgetMobileProps {
  onSubmit?: BookingWidgetContentProps['onSubmit']
}

export default function BookingWidgetMobile({
  onSubmit,
}: BookingWidgetMobileProps) {
  const {
    addresses,
    areAutoCompleteItemsLoading = [],
    autoCompleteItems = [],
    getData,
    loadingServiceLevels,
    onInputSearchTermHandlers = [],
    onSelectAutoCompleteItemHandlers = [],
    serviceOptions = [],
    totalPriceFormatted,
  } = useBookingWidgetStore()

  const { control, trigger, resetField } = useForm()

  const origin = useWatch({ control, name: 'origin' })
  const destination = useWatch({ control, name: 'destination' })

  // Check if route is unavailable (no services available or unsupported country)
  // Note: Pass false for hasAttemptedFetch since homepage booking widget doesn't select products
  const routeUnavailable = useMemo(() => {
    // First check if origin or destination is in an unsupported country
    if (hasUnsupportedCountry(addresses)) {
      return true
    }

    // Otherwise check if route is unavailable due to no services
    return isRouteUnavailable(
      addresses,
      origin,
      destination,
      serviceOptions,
      loadingServiceLevels,
      false, // Don't show unavailable on homepage - products not selected yet
    )
  }, [addresses, origin, destination, serviceOptions, loadingServiceLevels])

  const formatLocationValue = useCallback<
    Required<AutoCompleteInputProps>['displayValue']
  >(
    (data) =>
      data?.formatted_address.replaceAll('<br>', ', ') || data?.name || '',
    [],
  )

  const formatOption = useCallback((item: AutoCompleteItem) => {
    if (!item?.address?.city) return item?.formatted_address || item.name
    // GSE-11976: include address line 1 (street). The mapped autocomplete
    // address holds the street under `address1` (camelToSnakeCase leaves it
    // unchanged), so read it robustly — mirrors src/utils/autoComplete.ts.
    const addr = item.address as { address1?: string; address_1?: string }
    const street = addr.address1 ?? addr.address_1 ?? ''
    return [
      `${item.name} <br>`,
      street ? `${street},` : '',
      `${item.address?.city},`,
      item.address?.state,
      item.address?.zip,
    ]
      .filter(Boolean)
      .join(' ')
  }, [])

  const handleSubmit = useCallback(async () => {
    const isValid = await trigger(['origin', 'destination'])
    if (isValid) onSubmit?.(getData?.())
  }, [getData, onSubmit, trigger])

  return (
    <div className="flex flex-col gap-4 rounded-8 shadow-widget p-6 bg-white text-black w-full max-w-[510px] mx-auto">
      <div>
        {/* Trip type */}
        <div className="my-2">
          <TripTypeContent control={control} sticky={false} />
        </div>
        {/* Origin */}
        <AutoCompleteInput
          busy={areAutoCompleteItemsLoading?.[0]?.[0] || false}
          className="w-full [&_input]:md:mt-0"
          control={control}
          displayValue={formatLocationValue}
          leadingIcon="search"
          name="origin"
          onChange={onSelectAutoCompleteItemHandlers?.[0]?.[0]}
          onInput={onInputSearchTermHandlers?.[0]?.[0]}
          optionFormat={formatOption}
          options={autoCompleteItems?.[0]?.[0] || []}
          placeholder="Where from...?"
          autoComplete="off"
          address={addresses?.[0]?.[0] || null}
          resetField={resetField}
          rules={autoCompleteInputRules(
            addresses?.[0]?.[0] || null,
            addresses?.[0]?.[1] || null,
          )}
        />
        {/* Destination */}
        <AutoCompleteInput
          busy={areAutoCompleteItemsLoading?.[0]?.[1] || false}
          className="w-full [&_input]:md:mt-0"
          control={control}
          displayValue={formatLocationValue}
          leadingIcon="search"
          name="destination"
          onChange={onSelectAutoCompleteItemHandlers?.[0]?.[1]}
          onInput={onInputSearchTermHandlers?.[0]?.[1]}
          optionFormat={formatOption}
          options={autoCompleteItems?.[0]?.[1] || []}
          placeholder="Where to...?"
          autoComplete="off"
          address={addresses?.[0]?.[1] || null}
          resetField={resetField}
          rules={autoCompleteInputRules(
            addresses?.[0]?.[0] || null,
            addresses?.[0]?.[1] || null,
          )}
        />
        {/* Submit button or route unavailable message */}
        {routeUnavailable ? (
          <div className="sm-max:mt-4">
            <Notification
              size="small"
              icon={{ icon: 'info-circle-filled' }}
              type="info"
              title="Please contact customer support at 855-867-9915 to complete your booking."
              className="!text-left"
              dismissible
            />
          </div>
        ) : (
          <Button
            className="sm-max:mt-4 sm-max:w-full md:w-[150px] md:h-12 whitespace-nowrap"
            disabled={
              !origin || !destination || hasUnsupportedCountry(addresses)
            }
            onClick={handleSubmit}
            type="button"
          >
            Get started
          </Button>
        )}
      </div>
      <div className="flex justify-center gap-2">
        <div className="type-body-5 pt-4">From</div>
        <div className="flex flex-col items-center">
          <span className="type-display-2 text-theme-accessibility leading-none">
            {totalPriceFormatted}
          </span>
          <span className="type-body-5 text-neutral-600">each way</span>
        </div>
      </div>
    </div>
  )
}
